/**
 * Fila Refresh Context
 *
 * Contexto para fornecer funções de refresh de requests e websocket
 * para componentes que precisam dessas funcionalidades
 */

import { createContext, useContext, type ReactNode, useRef } from 'react';

interface FilaRefreshContextType {
  // Funções para refresh de dados
  refreshRequests: () => void;
  reconnectWebSocket: () => void;

  // Funções para registrar os callbacks
  registerRefreshCallback: (callback: () => void) => void;
  registerWebSocketCallback: (callback: () => void) => void;
}

const FilaRefreshContext = createContext<FilaRefreshContextType | null>(null);

interface FilaRefreshProviderProps {
  children: ReactNode;
}

export function FilaRefreshProvider({ children }: FilaRefreshProviderProps) {
  // Refs para armazenar as funções de callback
  const refreshCallbackRef = useRef<(() => void) | null>(null);
  const webSocketCallbackRef = useRef<(() => void) | null>(null);

  // Registrar callbacks
  const registerRefreshCallback = (callback: () => void) => {
    refreshCallbackRef.current = callback;
  };

  const registerWebSocketCallback = (callback: () => void) => {
    webSocketCallbackRef.current = callback;
  };

  // Funções que podem ser chamadas por qualquer componente
  const refreshRequests = () => {
    if (refreshCallbackRef.current) {
      console.log('🔄 FilaRefreshContext: Triggering request refresh');
      refreshCallbackRef.current();
    } else {
      console.warn('⚠️ FilaRefreshContext: No refresh callback registered');
    }
  };

  const reconnectWebSocket = () => {
    if (webSocketCallbackRef.current) {
      console.log('🔄 FilaRefreshContext: Triggering WebSocket reconnection');
      webSocketCallbackRef.current();
    } else {
      console.warn('⚠️ FilaRefreshContext: No WebSocket callback registered');
    }
  };

  const value: FilaRefreshContextType = {
    refreshRequests,
    reconnectWebSocket,
    registerRefreshCallback,
    registerWebSocketCallback,
  };

  return (
    <FilaRefreshContext.Provider value={value}>
      {children}
    </FilaRefreshContext.Provider>
  );
}

/**
 * Hook para usar o contexto de refresh da fila
 */
export function useFilaRefresh() {
  const context = useContext(FilaRefreshContext);

  if (!context) {
    throw new Error('useFilaRefresh must be used within a FilaRefreshProvider');
  }

  return context;
}